import { useState, useEffect } from 'react';

export const useBottlingCalculations = (materialsByType) => {
  // Define calculation functions that can be reused in components
  const calculateEstimatedCases = (gallons, bottleId) => {
    // Add console logs for debugging
    console.log(`Calculating for gallons=${gallons}, bottleId=${bottleId}`);
    
    if (!gallons || !bottleId) return 0;
    
    const gallonsNum = parseFloat(gallons);
    const bottleIdNum = parseInt(bottleId);
    
    if (isNaN(gallonsNum) || isNaN(bottleIdNum)) return 0;
    
    // Find the bottle in the materials
    const bottle = materialsByType.bottles?.find(b => b.materialID === bottleIdNum);
    console.log("Found bottle:", bottle);
    
    if (!bottle) return 0;
    
    // Parse the bottle volume
    const bottleVolume = parseFloat(bottle.bottleVolume) || 750;
    console.log(`Bottle volume: ${bottleVolume} ml`);
    
    // Calculate total milliliters
    const totalMilliliters = gallonsNum * 3785.41;
    console.log(`Total milliliters: ${totalMilliliters} ml`);
    
    // For large format containers (kegs)
    if (bottleVolume >= 3000) {
      // For kegs or large format, calculate the number of units directly
      const units = Math.ceil(totalMilliliters / bottleVolume);
      console.log(`LARGE FORMAT: ${totalMilliliters} ml / ${bottleVolume} ml = ${units} units`);
      return units;
    }
    
    // For regular bottles, calculate cases
    const totalBottles = Math.floor(totalMilliliters / bottleVolume);
    const bottlesPerCase = bottleVolume <= 375 ? 24 : 12;
    const cases = Math.floor(totalBottles / bottlesPerCase);
    console.log(`Regular bottles: ${totalBottles} bottles / ${bottlesPerCase} per case = ${cases} cases`);
    
    return cases;
  };

  const calculateMaterialsNeeded = (item) => {
    if (!item.bottleID || !item.gallonsPlanned) {
      return [];
    }
  
    const result = [];
    const casesEstimated = calculateEstimatedCases(parseFloat(item.gallonsPlanned), parseInt(item.bottleID));
    
    // Find materials in the material store
    const bottle = materialsByType.bottles.find(b => String(b.materialID) === String(item.bottleID));
    const cork = item.corkID ? materialsByType.corks.find(c => String(c.materialID) === String(item.corkID)) : null;
    const capsule = item.capsuleID ? materialsByType.capsules.find(c => String(c.materialID) === String(item.capsuleID)) : null;
    const label = item.labelID ? materialsByType.labels.find(l => String(l.materialID) === String(item.labelID)) : null;
    
    // Calculate bottles needed (usually 12 per case)
    if (bottle) {
      const required = casesEstimated * 12;
      result.push({
        id: bottle.materialID,
        name: bottle.name,
        required,
        available: parseFloat(bottle.currentStock),
        unit: bottle.unitOfMeasure || "Each",
        enoughStock: parseFloat(bottle.currentStock) >= required
      });
    }
    
    // Calculate corks needed
    if (cork) {
      const required = casesEstimated * 12;
      result.push({
        id: cork.materialID,
        name: cork.name,
        required,
        available: parseFloat(cork.currentStock),
        unit: cork.unitOfMeasure || "Each",
        enoughStock: parseFloat(cork.currentStock) >= required
      });
    }
    
    // Calculate capsules needed
    if (capsule) {
      const required = casesEstimated * 12;
      result.push({
        id: capsule.materialID,
        name: capsule.name,
        required,
        available: parseFloat(capsule.currentStock),
        unit: capsule.unitOfMeasure || "Each",
        enoughStock: parseFloat(capsule.currentStock) >= required
      });
    }
    
    // Calculate labels needed (with buffer for each wine)
    if (label) {
      const required = (casesEstimated * 12) + 50; // Add 50 extra labels
      result.push({
        id: label.materialID,
        wineID: item.wineID,
        wineName: item.varietal || `Wine ID: ${item.wineID}`,
        name: `${label.name} - ${item.varietal || `Wine ID: ${item.wineID}`}`,
        required,
        available: 0, // Labels don't use inventory
        unit: label.unitOfMeasure || "Each",
        enoughStock: false, // Always need to order labels
        needsOrdering: true
      });
    }
    
    return result;
  };

  return {
    calculateEstimatedCases,
    calculateMaterialsNeeded
  };
};